import { useEffect, useState } from 'react';

import useDC from '@eneco/dc/useDC';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useFormatter } from '@eneco/i18n';

import { useFlowTracking } from './useFlowTracking';
import { useCachedOfferMachine } from './useOfferMachine';
import { FlowConsumerContextEnergy } from '../../context';
import { getOffer, getCashbackTexts } from '../helpers';

interface UseOverviewStepLogicOptions {
  promotionTexts?: Exclude<Parameters<typeof getCashbackTexts>[1], undefined>;
  giftData?: Exclude<Parameters<typeof getCashbackTexts>[2], undefined>;
}

/**
 * Shared offer-fetch/checkout wiring for OverviewStep (eneco) and OverviewStepOxxio — both dispatch the
 * same GET_OFFER-on-mount / begin-checkout-on-submit / view-cart-on-success sequence around the cached
 * offer machine. The two components' Overview JSX composition (header, notifications, footer) still
 * differs enough per brand that it stays in each component rather than being forced into a shared shell.
 */
export const useOverviewStepLogic = ({ promotionTexts, giftData }: UseOverviewStepLogicOptions = {}) => {
  const { businessUnit, label } = useDC();
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const sendToFlowMachine = useFlowActorRef().send;

  const {
    context: flowContext,
    context: { isSME },
  } = flowState;

  const { offerState, sendToOfferMachine } = useCachedOfferMachine(businessUnit, label);
  const offer = offerState?.context?.offer;

  const { trackBeginCheckout, trackViewCart } = useFlowTracking();
  const { format, currency } = useFormatter();

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);

  const selectedOffer = getOffer(flowContext, offer);
  const selectedCashbackTexts = getCashbackTexts(
    selectedOffer?.costDiscounts?.cashback?.type,
    promotionTexts,
    giftData,
    selectedOffer?.costDiscounts?.cashback?.value,
  );

  const costsType: 'vatExcluded' | 'vatIncluded' = isSME ? 'vatExcluded' : 'vatIncluded';

  useEffect(() => {
    sendToOfferMachine({
      type: 'GET_OFFER',
      flowContext: { ...flowContext },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful) {
      if (selectedOffer) trackBeginCheckout(selectedOffer, offer);

      sendToFlowMachine({ type: 'NEXT_PAGE' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches('FETCHING'));
      setIsFailed(!offerState.matches('FETCHING') && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
    }
    if (offerState.matches('SUCCESS')) {
      if (selectedOffer) trackViewCart(selectedOffer, offer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerState]);

  return {
    flowContext,
    sendToFlowMachine,
    selectedOffer,
    selectedCashbackTexts,
    costsType,
    format,
    currency,
    isSubmitSuccessful,
    setIsSubmitSuccessful,
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
  };
};
